import type { KeyboardEvent, ReactNode } from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';

import { motion } from 'framer-motion';

interface SlideTabProps {
  tabs: (string | ReactNode)[];
  activeIndex?: number;
  onChange?: (index: number) => void;
  className?: string;
}

export const SlideTab = ({ tabs, activeIndex = 0, onChange, className = '' }: SlideTabProps) => {
  const [selected, setSelected] = useState(activeIndex);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // (탭 언더바 폭 확인, 3사 정합 조사) — `tab.offsetWidth`가 곧 "셀 100%"와 같은 값이다.
  // 그래서 코드는 그대로 둔다(고칠 게 없다). 이 사실은 소스만 읽어서는 안 보인다 — 실측으로
  // 확정한 것: 이 버튼들은 `flex` 행 안에서 `shrink-0`(줄어들지 않음)이고 `grow`가 없어
  // 서로 다닥다닥 붙는다(DOM 실측: 인접 버튼 사이 간격 0px, /board/idea 2탭·
  // /market/KRW-BTC 모바일 7탭 전부). 즉 "셀"(다음 탭 시작 전까지 확보된 폭)과 "콘텐츠
  // 박스"(라벨+`px-4` 패딩, `box-sizing: border-box`)가 완전히 같은 값이라 `offsetWidth`
  // 만으로 이미 셀 전체다 — 별도로 "셀의 100%로 맞추는" 작업이 필요 없다.
  //   /board/idea: Ideas 66px(라벨34+패딩32) · Feed 63px(31+32) — 인디케이터폭 66px/63px, 일치
  //   /market 모바일: Chart 68 · Order Book 104 · Info 56 · Feed 63 · Ratings 80 · Poll 55 ·
  //     Derivatives 103 — 7개 전부 인디케이터폭이 offsetWidth와 소수점 이하만 다를 만큼 일치
  //     (framer-motion spring 보간 중 캡처된 서브픽셀 오차, 정착 후엔 완전 일치)
  // 🔴 앱(iOS/android)은 셀이 라벨보다 2.5~4.9배 넓어(예: 커뮤니티 "홈" 탭 라벨/셀 비율
  // 35%) 같은 "언더바=콘텐츠폭" 공식이 화면마다 다르게 보이는 결함이 있다(designer 실측,
  // TRAD-3055 논의) — web은 애초에 셀이 콘텐츠 폭이라 이 문제가 구조적으로 없다. "앱을
  // web처럼 바꿀지"는 별건 결정이고, 여기서는 web이 이미 그 상태라는 사실만 고정한다.
  const updateIndicator = useCallback(() => {
    const tab = tabRefs.current[selected];
    if (tab) {
      setIndicatorStyle({ left: tab.offsetLeft, width: tab.offsetWidth });
      // 탭이 많아 가로 스크롤될 때 활성 탭을 보이게 유지(세로 점프 방지: block nearest)
      tab.scrollIntoView?.({ inline: 'center', block: 'nearest' });
    }
  }, [selected]);

  useEffect(() => {
    updateIndicator();
  }, [updateIndicator]);

  useEffect(() => {
    setSelected(activeIndex);
  }, [activeIndex]);

  const handleSelect = (index: number) => {
    setSelected(index);
    onChange?.(index);
  };

  // TRAD-3137 — 탭 시맨틱(WAI-ARIA Tabs 패턴). 예전엔 role/aria 가 없어 스크린리더에 «버튼 나열» 로 읽혔고
  // (몇 개 중 몇 번째·무엇이 선택됐는지 안 들림) 키보드는 Tab 으로 모든 탭을 하나씩 지나야 했다.
  //  - role=tablist / role=tab + aria-selected
  //  - roving tabindex: 선택된 탭만 Tab 정거장(tabIndex 0), 나머지는 -1 → Tab 한 번에 탭 목록을 빠져나간다
  //  - ←/→(순환) · Home/End 는 **포커스만** 옮긴다. 선택은 Enter/Space(수동 활성화).
  //    🔴 자동 활성화(이동 즉시 선택)로 두지 않는 이유: 소비자 중 탭이 라우트를 바꾸는 화면(BoardLayout 등)이 있어
  //    화살표 한 번이 페이지 이동이 된다 — 탭 목록을 훑는 키보드 사용자가 의도치 않게 화면을 떠난다(2026-10-04 팀장 결정).
  // 🔴 못 하는 것: 패널은 소비자가 그리므로 `aria-controls`/`role=tabpanel` 연결은 여기서 못 건다(소비자별 후속).
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      // 버튼의 기본 동작(Enter/Space → click)에 기대지 않고 직접 선택한다 — preventDefault 로 click 이중 발화를 막는다.
      event.preventDefault();
      handleSelect(index);
      return;
    }
    const last = tabs.length - 1;
    const next =
      event.key === 'ArrowRight'
        ? index === last
          ? 0
          : index + 1
        : event.key === 'ArrowLeft'
          ? index === 0
            ? last
            : index - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (next === null) return;
    event.preventDefault();
    tabRefs.current[next]?.focus();
  };

  return (
    <div role="tablist" className={`relative flex overflow-x-auto border-b border-border ${className}`}>
      {tabs.map((tab, index) => (
        <button
          key={index}
          type="button"
          role="tab"
          aria-selected={selected === index}
          tabIndex={selected === index ? 0 : -1}
          onKeyDown={event => handleKeyDown(event, index)}
          ref={el => {
            tabRefs.current[index] = el;
          }}
          onClick={() => handleSelect(index)}
          // TRAD-3136 — 라벨색 3사 정합 + 접근성. `text-foreground`(선택)는 대비가 좋았지만
          // web 단독 값이라 갈렸다(iOS/android는 브랜드 텍스트색) — `text-brand-text`로
          // 맞춘다. `theme-primary`가 아니라 `brand-text`인 이유: web 다크는 theme-primary
          // 대비가 3.48:1로 미달, brand-text(다크 #9864F1)는 이미 갈려 있어 5.12:1 통과
          // (팀장 실측). `text-grey-60`(비선택)은 라이트 2.68:1·다크 3.99:1로 WCAG 4.5:1
          // 미달 — hover:text-grey-80(6.19/9.75)만 통과해 마우스 없는 기기(이 앱은 모바일
          // 주력)에서 영원히 미달이었다. `text-muted-foreground`(라이트 5.05/다크 5.87)로
          // 교체 — web 새 값을 안 만들고 앱 값(#656C74)과 다크가 바이트 동일인 기존 토큰.
          className={`shrink-0 whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors ${
            selected === index ? 'text-brand-text' : 'text-muted-foreground'
          }`}
        >
          {tab}
        </button>
      ))}
      {/* TRAD-3055 — 2026-09-03 davidyoon 실기기 검수에서 TRAD-3074의 "탭 밑줄 중성색"
          결정이 번복됐다. 번복 사유: 중성화가 SlideTab을 쓰는 화면(~~11곳~~ 🔴 2026-09-04
          정정(TRAD-3136 조사) — 실제 렌더 호출부는 9파일·런타임 마운트는 14화면. "11"의
          출처를 못 찾았다 — 이 주석 자신이 재인용된 것일 수 있다, 세는 단위를 안 밝힌 값은
          믿지 말 것)에만 적용되고
          같은 화면 안 다른 탭(MarketDetail.tsx의 border-primary 언더라인 탭 등)·다른
          컴포넌트(FilterChip·BottomTabBar·AppTopNav)는 전부 브랜드색 그대로라 탭끼리
          갈렸다 — iOS 실측(섹션탭 4곳만 중성화, PredictionTabButtonView 등 나머지는
          브랜드색 그대로)도 같은 결함이었다. davidyoon: "여기만 검은색이면 이상하잖아."
          bg-foreground(중성) → bg-theme-primary(브랜드)로 되돌린다. 근거 문서:
          docs-ops/2026-09-01-TRAD-3050-purple-design-spec.html §05
          "✔ 2026-09-03 번복 — 탭 활성 표시는 다시 브랜드색이다".
          🔴 FilterChip.tsx의 선택 상태(bg-primary)는 이번에도 건드리지 않는다 —
          TRAD-2110이 확정한 별도 규칙(탭≠칩), 이번 번복과 무관. */}
      <motion.div
        aria-hidden="true"
        className="absolute bottom-0 h-0.5 bg-theme-primary"
        animate={{ left: indicatorStyle.left, width: indicatorStyle.width }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      />
    </div>
  );
};
